export function dateToTimestamp(dateString: string): number {
  const [datePart, timePart] = dateString.split(' ');
  const [day, month, year] = datePart.split('.').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  const date = new Date(year, month - 1, day, hours, minutes);

  if (isNaN(date.getTime())) {
    return new Date(dateString).getTime();
  }

  return date.getTime();
}

export function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();

  const formattedDay = day < 10 ? '0' + day : day;
  const formattedMonth = month < 10 ? '0' + month : month;
  const formattedHours = hours < 10 ? '0' + hours : hours;
  const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;

  const formattedDate = `${formattedDay}.${formattedMonth}.${year} ${formattedHours}:${formattedMinutes}`;

  return formattedDate;
}

export function calendarFormatDateString(date: Date): string {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${month}.${day}.${year} ${hours}:${minutes}`;
}

export function parseFormattedDateString(formattedDateString: string): Date {
  const [datePart, timePart] = formattedDateString.split(' ');
  const [month, day, year] = datePart
    .split('/')
    .map((num) => parseInt(num, 10));
  const [hours, minutes] = timePart.split(':').map((num) => parseInt(num, 10));

  const date = new Date(year, month - 1, day, hours, minutes);

  return date;
}

export function formatDateStringFromRaw(
  dateString: string,
  dateAndTimeSeparator?: string
): string {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}.${month}.${year} ${
    (typeof dateAndTimeSeparator === 'string'
      ? dateAndTimeSeparator + ' '
      : '') + hours
  }:${minutes}`;
}

export const isoToCustomFormat = (isoString: string): string => {
  const date = new Date(isoString);
  return formatDateStringFromRaw(date.toISOString());
};

export function getLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function timestampToFormattedDate(
  timestamp?: string | number | null
): Date | undefined {
  if (!timestamp) return undefined;
  const date = new Date(Number(timestamp));
  return isNaN(date.getTime()) ? undefined : date;
}

export function parseInputDate(input: string): Date | null {
  const formats = [
    /(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})/,
    /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/
  ];

  for (const regex of formats) {
    const match = input.match(regex);
    if (match) {
      let [, day, month, year, hours, minutes] = match.map(Number);

      year = Math.max(1900, Math.min(2099, year));

      month = Math.max(1, Math.min(12, month));

      const lastDayOfMonth = new Date(year, month, 0).getDate();
      day = Math.max(1, Math.min(lastDayOfMonth, day));

      hours = Math.max(0, Math.min(23, hours));
      minutes = Math.max(0, Math.min(59, minutes));

      const originalHours = Number(match[4]);
      const originalMinutes = Number(match[5]);
      if (originalHours > 23 || originalMinutes > 59) {
        hours = 23;
        minutes = 59;
      }

      return new Date(year, month - 1, day, hours, minutes);
    }
  }

  const parsed = new Date(input);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseYYYYMMDD = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const setEndOfDay = (date: Date): Date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
};

export function normalizeScheduleTimeToHHMM(t: string): string {
  return (t ?? '').trim().substring(0, 5);
}
